import React, { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import useClasses from '../../../../../hooks/classes/useClasses'
import { useNavigate } from 'react-router-dom'
import FormBasicInformation from '../FormBasicInformation/FormBasicInformation'
import FormSubject from '../FormSubject/FormSubject'
import FormCurriculumElements from '../FormCurriculumElements/FormCurriculumElements'
import CardKeyValue from '../../../../card/cardKeyValue'
import { useSelector } from 'react-redux'
import { SelectMultipleFieldYears } from '../../../../FormControl/SelectRequest/SelectMultipleFieldYears'
import { Box, Grid } from '@mui/material'
import { capitalizarPrimeraLetra } from '../../../../../utils'

const DialogAction = ({ show, onClose, type, formData = {}, paramsCurriculumId }) => {
  const types = [
    'archive',
    'delete',
    'basic-information',
    'subject',
    'curriculum-elements',
    'batch-curriculum-elements',
    'batch-subject',
    'batch-year-level'
  ]
  const years = useSelector((state) => state?.tenant?.years || [])

  const navigate = useNavigate()

  const [loading, toggleLoading] = useState(false)
  const [errors, setInputErrors] = useState({})
  const [form, setInputForm] = useState({ ...formData })
  const { deleteClass, archiveClass, updateClass, batchUpdateClasses } = useClasses()

  const onChange = (name, value) => {
    if (name === 'curriculumId') {
      const newForm = { ...form, [name]: value }
      if (String(value) !== String(formData.curriculumId)) {
        newForm.subjectId = ''
        newForm.curriculumElements = {}
      }
      setInputForm({ ...newForm })
      setInputErrors((old) => ({ ...old, [name]: null }))
    } else if (name === 'curriculumElements') {
      const newForm = { ...form, [name]: value }
      if (JSON.stringify(value) !== JSON.stringify(formData.curriculumElements)) {
        newForm.subjectId = ''
      }
      setInputForm({ ...newForm })
      setInputErrors((old) => ({ ...old, [name]: null }))
    } else {
      setInputForm({ ...form, [name]: value })
      setInputErrors((old) => ({ ...old, [name]: null }))
    }
  }

  const onConfirm = async (e) => {
    if (!types.includes(type)) {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    if (form._id === undefined) {
      setInputErrors({ message: 'Invalid id' })
      return
    }

    e.preventDefault()
    toggleLoading(true)
    try {
      let result = { error: false }
      if (type === 'delete') {
        result = await deleteClass(paramsCurriculumId, form._id)
      }
      if (type === 'archive') {
        result = await archiveClass(paramsCurriculumId, form._id)
      }
      if (['basic-information', 'subject', 'curriculum-elements'].includes(type)) {
        result = await updateClass(paramsCurriculumId, form._id, type, form)
      }

      if (['batch-curriculum-elements', 'batch-subject', 'batch-year-level'].includes(type)) {
        result = await batchUpdateClasses(paramsCurriculumId, form._id, type.replace('batch-', ''), form)
      }

      if (result.error) {
        setInputErrors(result)
      } else {
        if (type === 'delete' || type === 'archive') {
          navigate(`/dashboard/classes/${paramsCurriculumId}`)
        }
        onClose()
      }
    } finally {
      toggleLoading(false)
    }
  }

  useEffect(() => {
    setInputForm({ ...formData })
    if (!formData._id) {
      setInputErrors({ message: 'Invalid id' })
    }
    if (formData.error) {
      setInputErrors({ message: formData.error })
    }
    if (['subject', 'curriculum-elements'].includes(type) && form?.unitOfLearnings?.length > 0) {
      let typeText = type
      if (type === 'basic-information') {
        typeText = 'year level'
      }
      setInputErrors({
        warning: `${capitalizarPrimeraLetra(
          typeText.replace('-', ' ')
        )} cannot be edited. Units of Learning are linked to this class`
      })
    }
    if (['basic-information'].includes(type) && form?.unitOfLearnings?.length > 0) {
      let typeText = type
      if (type === 'basic-information') {
        typeText = 'year level'
      }
      setInputErrors({
        warningYearLevel: `${capitalizarPrimeraLetra(
          typeText.replace('-', ' ')
        )} cannot be edited. Units of Learning are linked to this class`
      })
    }

    if (
      ['batch-curriculum-elements', 'batch-subject', 'batch-year-level'].includes(type) &&
      form?.unitOfLearnings?.length > 0
    ) {
      let typeText = type
      if (type.startsWith('batch-')) {
        typeText = type.replace('batch-', '')
      }

      setInputErrors((old) => ({
        ...old,
        warning: `${capitalizarPrimeraLetra(
          typeText.replace('-', ' ')
        )} cannot be edited. Units of Learning are linked to this class`
      }))
    }
  }, [formData, type])
  const checkConfirm = () => {
    if (types.includes(type) && form._id) {
      // if (
      //   ['subject', 'batch-subject'].includes(type) &&
      //   (!form.curriculumId || form.curriculumId === 'unassigned')
      // ) {
      //   return false;
      // }
      if (
        [
          'subject',
          'curriculum-elements',
          'batch-curriculum-elements',
          'batch-subject',
          'batch-year-level'
        ].includes(type) &&
        form?.unitOfLearnings?.length > 0
      ) {
        return false
      }

      if (
        ['batch-curriculum-elements', 'batch-subject', 'batch-year-level'].includes(type) &&
        formData.error
      ) {
        return false
      }

      return true
    }

    return false
  }

  const typeForm = [
    'basic-information',
    'subject',
    'curriculum-elements',
    'batch-curriculum-elements',
    'batch-subject',
    'batch-year-level'
  ]
  const title = ['basic-information', 'subject', 'curriculum-elements'].includes(type)
    ? `Edit ${type.replace('-', ' ')}`
    : ['batch-curriculum-elements', 'batch-subject', 'batch-year-level'].includes(type)
      ? `Batch edit ${type.replace('batch-', ' ').replace('-', ' ')}`
      : type

  return (
    <DialogConfirmCancel
      id={`class-${type}`}
      open={show}
      onCancel={onClose}
      onConfirm={checkConfirm() ? onConfirm : undefined}
      textConfirm={typeForm.includes(type) ? 'Save' : 'Confirm'}
      loading={loading}
      title={title}
      onCloseHeader={true}
      maxWidth='xs'
    >
      {types.includes(type) && (
        <>
          {typeForm.includes(type) ? (
            <>
              {['curriculum-elements', 'subject'].includes(type) && (
                <div className='flex flex-wrap gap-1 pb-4 items-center justify-center'>
                  {form.code && (
                    <CardKeyValue
                      title='code'
                      className={`min-w-100px max-w-150px`}
                      value={<div>{form.code}</div>}
                    />
                  )}
                  {form.name && (
                    <CardKeyValue
                      title='Name'
                      className={`min-w-100px max-w-150px`}
                      value={<div>{form.name}</div>}
                    />
                  )}
                  {form.yearLevel && (
                    <CardKeyValue
                      title='Year level'
                      className={`min-w-100px max-w-150px`}
                      value={
                        <div>
                          {years
                            ?.filter((year) => form?.yearLevel?.includes(year._id))
                            ?.map((year) => year.name)
                            .join(', ')}
                        </div>
                      }
                    />
                  )}
                </div>
              )}
              {['batch-curriculum-elements', 'batch-subject', 'batch-year-level'].includes(type) &&
                !formData.error && (
                  <div className='flex flex-wrap gap-1 pb-4 items-center justify-center'>
                    {form?.classes?.map((item, index) => (
                      <CardKeyValue
                        key={index}
                        title='Class'
                        className={`min-w-100px max-w-150px`}
                        value={<div>{item.code}</div>}
                      />
                    ))}
                  </div>
                )}
              {type === 'basic-information' && !formData.error && (
                <FormBasicInformation form={form} errors={errors} onChange={onChange} />
              )}
              {type === 'batch-year-level' && !formData.error && (
                <Box className=''>
                  <Grid container spacing={2} alignItems='center'>
                    <Grid item sm={3}>
                      <Typography
                        variant='inherit'
                        className='font-bold capitalize'
                        color='primary'
                        align='right'
                        component='p'
                      >
                        Year level
                      </Typography>
                    </Grid>
                    <Grid item sm={9}>
                      {checkConfirm() ? (
                        <SelectMultipleFieldYears
                          id='yearLevel'
                          name='yearLevel'
                          error={errors.yearLevel}
                          helperText={errors.yearLevel}
                          value={form.yearLevel}
                          onChange={(e) => onChange('yearLevel', e.target.value)}
                          isPreview={false}
                        />
                      ) : (
                        <Typography className=' text-muted-foreground opacity-50'>None </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
              {['subject', 'batch-subject'].includes(type) && !formData.error && (
                <>
                  {/* {!form.curriculumId || form.curriculumId === 'unassigned' ? (
                    <Alert severity='warning'>Please assign a curriculum first</Alert>
                  ) : (
                    <FormSubject
                      form={form}
                      errors={errors}
                      onChange={onChange}
                      curriculumId={paramsCurriculumId}
                    />
                  )} */}
                  <FormSubject
                    form={form}
                    errors={errors}
                    onChange={onChange}
                    curriculumId={paramsCurriculumId}
                  />
                </>
              )}
              {['curriculum-elements', 'batch-curriculum-elements'].includes(type) && !formData.error && (
                <FormCurriculumElements
                  form={form}
                  errors={errors}
                  onChange={onChange}
                  curriculumId={paramsCurriculumId}
                />
              )}
            </>
          ) : (
            <div>
              <Typography>
                Are you sure you want to {type} the class <b>{form.name || form.code}</b>?
              </Typography>
            </div>
          )}
        </>
      )}
      {errors?.message && (
        <Alert className='mt-4' severity='error'>
          {errors?.message}
        </Alert>
      )}
      {errors?.warning && (
        <Alert className='mt-4' severity='warning'>
          {errors?.warning}
        </Alert>
      )}
    </DialogConfirmCancel>
  )
}

export default DialogAction
